import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { VerificationStepTrackingLabels } from 'constants/trackingLabels'
import { VerificationRoutes } from 'constants/verification'
import { FinpromStepKey } from 'pages/Finprom/types'
import { UnsavedChangesModalProps } from 'pages/Verification/_components/UnsavedChangesModal'
import {
  DynamicDocumentsDataStepList,
  MultipleAccountContactsStepList,
  VerificationRouteKey,
  VerificationStep,
} from 'types/verification'
import { trackOnboarding } from 'utils/tracker'
import { getNextPendingStepKey, getNextStepKey } from 'utils/verification'
import { useFinpromContext } from './context/useFinpromContext'

export type ClickMenuItemFn<KeyType> = (newStep: KeyType) => void

export type VerificationStepsNavigationData = {
  verificationSteps: VerificationStep[]
  isLoadingStepsData: boolean
  refetchBusinessVerificationStatus: () => void
  refetchDynamicDocuments: () => void
}

export type VerificationStepsNavigationReturn = {
  activeStep: VerificationRouteKey
  unsavedChanges: UnsavedChangesModalProps
  navigationContext: {
    setActiveStep: Dispatch<SetStateAction<VerificationRouteKey>>
    setHasUnsavedChanges: Dispatch<SetStateAction<boolean>>
    onSubmitStep: () => void
  }
  menuActions: {
    onClickMenuItem: ClickMenuItemFn<VerificationRouteKey>
    onClickFinpromMenuItem: ClickMenuItemFn<FinpromStepKey>
  }
}

export const useVerificationStepsNavigation = ({
  verificationSteps,
  isLoadingStepsData,
  refetchBusinessVerificationStatus,
  refetchDynamicDocuments,
}: VerificationStepsNavigationData): VerificationStepsNavigationReturn => {
  const [activeStep, setActiveStep] = useState<VerificationRouteKey>()
  const [newStep, setNewStep] = useState<VerificationRouteKey>()
  const [isInitialActiveStepLoaded, setIsInitialActiveStepLoaded] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  const {
    activeStep: activeFinpromStep,
    isGoBackToBusinessAddress,
    isWatchingBusinessAddress,
    setActiveStep: setActiveFinpromStep,
    onLeaveBusinessAddress,
    onGoBackToBusinessAddress,
  } = useFinpromContext()

  const goToNextPendingStep = () => {
    const nextPendingStepKey = getNextPendingStepKey(verificationSteps)
    nextPendingStepKey && setActiveStep(nextPendingStepKey)
  }

  const goToNextStep = () => {
    const nextStepKey = getNextStepKey(verificationSteps, activeStep)
    nextStepKey && setActiveStep(nextStepKey)
  }

  const handleSelectNewStep = (newStepKey: VerificationRouteKey) => {
    if (newStepKey === activeStep) return
    if (isWatchingBusinessAddress && activeStep === VerificationRoutes.BUSINESS_ADDRESS) {
      onLeaveBusinessAddress()
    }
    if (MultipleAccountContactsStepList?.includes(activeStep)) {
      refetchDynamicDocuments()
    }
    hasUnsavedChanges ? setNewStep(newStepKey) : setActiveStep(newStepKey)
  }

  const onNewStepStay = () => {
    setNewStep(null)
  }

  const onNewStepLeave = () => {
    setHasUnsavedChanges(false)
    setActiveStep(newStep)
  }

  const onClickMenuItem: ClickMenuItemFn<VerificationRouteKey> = newStepKey => {
    handleSelectNewStep(newStepKey)
    if (activeFinpromStep) {
      setActiveFinpromStep(null)
    }
  }

  const onClickFinpromMenuItem: ClickMenuItemFn<FinpromStepKey> = newStepKey => {
    setActiveFinpromStep(newStepKey)
  }

  const onSubmitStep = () => {
    trackOnboarding(`Continue - ${VerificationStepTrackingLabels?.[activeStep]}`)
    setHasUnsavedChanges(false)
    goToNextStep()
    refetchBusinessVerificationStatus()
    if (DynamicDocumentsDataStepList?.includes(activeStep)) {
      refetchDynamicDocuments()
    }
  }

  useEffect(() => {
    if (!isLoadingStepsData && !isInitialActiveStepLoaded) {
      setIsInitialActiveStepLoaded(true)
      goToNextPendingStep()
    }
  }, [isLoadingStepsData, isInitialActiveStepLoaded])

  useEffect(() => {
    setNewStep(null)
  }, [activeStep])

  useEffect(() => {
    if (isGoBackToBusinessAddress && activeStep !== VerificationRoutes.BUSINESS_ADDRESS) {
      onGoBackToBusinessAddress()
      setActiveStep(VerificationRoutes.BUSINESS_ADDRESS)
    }
  }, [isGoBackToBusinessAddress, activeStep])

  return {
    activeStep,
    unsavedChanges: {
      isOpen: !!newStep,
      onLeave: onNewStepLeave,
      onStay: onNewStepStay,
    },
    menuActions: {
      onClickMenuItem,
      onClickFinpromMenuItem,
    },
    navigationContext: {
      setActiveStep,
      setHasUnsavedChanges,
      onSubmitStep,
    },
  }
}
